import { useCallback, useEffect, useMemo, useRef } from "react";

const getWorker = <T extends Worker>(worker: T | (() => T)) =>
  typeof worker === "function" ? worker() : worker;

const useWorker = <T extends Worker>(worker: T | (() => T)) => {
  const workerRef = useRef<T | null>(
    typeof window === "undefined" ? null : getWorker(worker),
  );

  useEffect(() => {
    workerRef.current = getWorker(worker);
    return () => {
      workerRef.current?.terminate();
    };
  }, [worker]);

  const postMessage = useCallback(
    async <
      V extends Parameters<NonNullable<T["onmessage"]>>[0]["data"] = Parameters<
        NonNullable<T["onmessage"]>
      >[0]["data"],
    >(
      data: Parameters<T["postMessage"]>[0],
    ) =>
      new Promise<MessageEvent<V | null>>((resolve, reject) => {
        if (!workerRef.current) {
          return reject(new Error("Worker unavailable"));
        }
        workerRef.current.onerror = reject;
        workerRef.current.onmessageerror = reject;
        workerRef.current.onmessage = resolve;
        workerRef.current.postMessage(data);
      }),
    [],
  );

  return useMemo(() => [postMessage, workerRef] as const, [postMessage]);
};

export default useWorker;
